/**
 * React Query Hook: Get Metadata Item
 * 
 * Fetches a single metadata item with its content
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { GET_METADATA_ITEM_QUERY } from '@/services/metadata_v1/queries';
import {
  GetMetadataItemResponse,
  GetMetadataItemVariables,
  MetadataItem,
} from '@/services/metadata_v1/types';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Fetch metadata item from API.
 *
 * Backend-emitted artifacts use `art_xxx` ids — they live in the
 * agentic-platform artifact store, not the Elixir metadata service.
 * For those, we hit `/api/artifacts/{id}` REST directly (same path
 * the chat rehydration uses). Salesforce metadata ids go through the
 * existing GraphQL resolver unchanged.
 */
async function fetchMetadataItem(
  variables: GetMetadataItemVariables
): Promise<MetadataItem> {
  const { id } = variables;
  if (typeof id === "string" && id.startsWith("art_")) {
    const { getMetadataItemById } = await import(
      "@/pages/dev-agent/devAgentService"
    );
    return (await getMetadataItemById(id)) as unknown as MetadataItem;
  }
  const response = await graphqlRequest<GetMetadataItemResponse>(
    GET_METADATA_ITEM_QUERY,
    variables
  );
  return response.metadataItem;
}

/**
 * Hook to fetch a single metadata item with its content
 * 
 * Automatically uses organization ID from selected organization
 * 
 * @param id - The ID of the metadata item to fetch
 * @param options - Query options
 * @returns React Query result with metadata item
 */
export function useMetadataItem(
  id: string | null | undefined,
  options?: {
    enabled?: boolean;
  }
): UseQueryResult<MetadataItem, Error> {
  const { enabled = true } = options || {};
  
  // Subscribe to selected organization from auth store
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  
  // Extract organizationId
  const organizationId = selectedOrg?.id;

  return useQuery({
    queryKey: ['metadataItem', organizationId, id],
    queryFn: () =>
      fetchMetadataItem({
        organizationId: organizationId!,
        id: id!,
      }),
    enabled: enabled && !!id && (id?.startsWith("art_") || !!organizationId),
    staleTime: 5 * 60 * 1000, // 5 minutes - file content is relatively stable
    refetchOnWindowFocus: false,
  });
}